import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FilesystemActivityService } from '../../../services/filesystem-activity.service';
import type {
    FilesystemActivityEntry,
    FilesystemActivityResponse,
    LaravelPaginationMeta,
} from '../../../models/api';
import type { Observable } from 'rxjs';
import { activityLabel, activityDescription } from '../../../shared/filesystem-activity-labels';

@Component({
    selector: 'app-file-manager-plugins',
    standalone: true,
    imports: [CommonModule, ButtonModule, DialogModule, TableModule, InputTextModule, FormsModule, IconFieldModule, InputIconModule],
    templateUrl: './file-manager-plugins.component.html',
    styleUrl: './file-manager-plugins.component.scss'
})
export class FileManagerPlugins {
    private readonly activityService = inject(FilesystemActivityService);

    showLogs = signal(false);
    showFileOps = signal(false);
    logFilterText = signal('');

    // FELTÖLTÉSI NAPLÓ → filesystem-activities/filesystem
    filesystemEntries = signal<FilesystemActivityEntry[]>([]);
    filesystemMeta = signal<LaravelPaginationMeta | null>(null);
    filesystemLoading = signal(false);

    // LINKELÉSI NAPLÓ → filesystem-activities/links
    linkEntries = signal<FilesystemActivityEntry[]>([]);
    linkMeta = signal<LaravelPaginationMeta | null>(null);
    linkLoading = signal(false);

    filteredFilesystemEntries = computed(() => this.filterEntries(this.filesystemEntries()));
    filteredLinkEntries = computed(() => this.filterEntries(this.linkEntries()));

    eventLabel(event: string | null | undefined): string {
        return activityLabel(event);
    }

    descriptionLabel(entry: FilesystemActivityEntry): string {
        return activityDescription(entry);
    }

    openUploadLog(): void {
        this.showLogs.set(true);
    }

    openLinkLog(): void {
        this.showFileOps.set(true);
    }

    /** PrimeNG lazy-load handler for the filesystem (upload) log table. */
    onFilesystemLazyLoad(event: TableLazyLoadEvent): void {
        this.loadFilesystemActivity(this.pageFromEvent(event));
    }

    /** PrimeNG lazy-load handler for the link log table. */
    onLinkLazyLoad(event: TableLazyLoadEvent): void {
        this.loadLinkActivity(this.pageFromEvent(event));
    }

    private loadFilesystemActivity(page: number): void {
        this.load(
            this.activityService.getFilesystemActivity(page),
            this.filesystemEntries,
            this.filesystemMeta,
            this.filesystemLoading,
            'filesystem',
        );
    }

    private loadLinkActivity(page: number): void {
        this.load(
            this.activityService.getLinkActivity(page),
            this.linkEntries,
            this.linkMeta,
            this.linkLoading,
            'links',
        );
    }

    private load(
        request$: Observable<FilesystemActivityResponse>,
        entries: ReturnType<typeof signal<FilesystemActivityEntry[]>>,
        meta: ReturnType<typeof signal<LaravelPaginationMeta | null>>,
        loading: ReturnType<typeof signal<boolean>>,
        label: string,
    ): void {
        loading.set(true);
        request$.subscribe({
            next: (res) => {
                entries.set(res.data ?? []);
                meta.set(res.meta ?? null);
                loading.set(false);
            },
            error: (err) => {
                console.error(`[filesystem-activities/${label}] error`, err);
                loading.set(false);
            },
        });
    }

    private pageFromEvent(event: TableLazyLoadEvent): number {
        const first = event.first ?? 0;
        const rows = event.rows || 15;
        return Math.floor(first / rows) + 1;
    }

    private filterEntries(entries: FilesystemActivityEntry[]): FilesystemActivityEntry[] {
        const filter = this.logFilterText().toLowerCase().trim();
        if (!filter) return entries;
        return entries.filter((e) =>
            this.descriptionLabel(e).toLowerCase().includes(filter) ||
            e.event?.toLowerCase().includes(filter) ||
            this.eventLabel(e.event).toLowerCase().includes(filter) ||
            e.causer?.name?.toLowerCase().includes(filter) ||
            e.causer?.email?.toLowerCase().includes(filter) ||
            e.subject?.name?.toLowerCase().includes(filter),
        );
    }
}
